<template>
  <div>
    <loading-screen v-show="isLoading" :content="loadingScreenContent" />
    <div class="content-box content-box-plus" v-show="!isLoading">
      <Dialog :show="errorDialogVisible">
        <div class="dialog-box">
          <font-awesome-icon
            :icon="['fas', 'exclamation-triangle']"
            class="m-auto fa-3x text-danger"
          />
          <h2 class="mt-2 mb-0">Error</h2>
          <p class="my-3">{{ errorDialogMessage }}</p>
          <div class="dialog-buttons">
            <button class="confirm-button" @click="$event => closeDialog()">OK</button>
          </div>
        </div>
      </Dialog>
      <div class="card-wrapper">
        <div class="card-body">
          <div class="mb-4">
            <div class="useredit-title-responsive d-flex align-items-center">
              <h1
                class="mr-2 title-h1"
                :class="selectedTitle.class"
                :style="{ ...selectedTitle.style, color: fulfill_sync_details ? partnerColor : '' }"
                v-safe-html="selectedTitle.text"
              />
              <h1
                class="title-h1 email-title"
                :class="selectedTitle.class"
                :style="{ ...selectedTitle.style, color: fulfill_sync_details ? partnerColor : '' }"
                v-if="!detailVerificationStep"
              >
                ({{ currentUser.email }})
              </h1>
            </div>
            <div>
              <span
                :class="selectedSubTitle.class"
                :style="selectedSubTitle.style"
                v-if="fulfill_sync_details"
                v-safe-html="selectedSubTitle.text"
              />
              <h4
                :class="selectedSubTitle.class"
                :style="selectedSubTitle.style"
                v-safe-html="selectedSubTitle.text"
                v-else
              />
            </div>
          </div>
          <div class="content-box content-box-plus child">
            <div class="card-wrapper">
              <div class="card-body">
                <user-form-fields
                  @update-success="redirectToBuildingLists()"
                  :isFulFillSync="isFulFillSync"
                  :detailVerificationStep="detailVerificationStep"
                  :isLoading="isLoading"
                  :conditionalDisplay="conditionalDisplay"
                  :conditionalDisplay2="conditionalDisplay2"
                  @loadingScreen="loadingScreen"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="content-box content-box-plus" v-if="fulfill_sync_details && !detailVerificationStep" v-show="contactCount > 0">
      <div class="card-body">
        <h1
          :class="contactSettings.attributes?.title.class"
          :style="{ ...contactSettings.attributes?.title.style, color: fulfill_sync_details ? partnerColor : '' }"
          v-safe-html="contactSettings.attributes?.title.text"
        />
        <div class="mb-2 pb-4 pt-2">
          <span
            :class="contactSettings.attributes?.subtitle.class"
            :style="contactSettings.attributes?.subtitle.style"
            v-if="fulfill_sync_details"
            v-safe-html="contactSettings.attributes?.subtitle.text"
          />
        </div>
        <contact
          :section="'user-profile'"
          @total-contacts="setContactNumber"
          :isUserEditPage="false"
          :userSelectedContactId="currentUser.extra_info?.selected_contact_id || ''"
        />
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-cycle
import router from '../../router/index';
import UserFormFields from '../../components/UserFormFields.vue';
import LoadingScreen from "@/components/LoadingScreen.vue";
import Contact from '../../components/Contact.vue';
import Dialog from '@/components/Dialog.vue';

export default {
  name: 'UserEdit',

  components: {
    LoadingScreen,
    UserFormFields,
    Contact,
    Dialog,
  },

  data() {
    return {
      selectedTitle: '',
      selectedSubTitle: '',
      isLoading: false,
      contactCount: 0,
      conditionalDisplay: false,
      conditionalDisplay2: false,
      errorDialogVisible: false,
      errorDialogMessage: '',
    }
  },

  created() {
    if(this.$route.query?.error_type === 'account_create_not_allowed') {
      this.errorDialogMessage = this.$route.query?.error;
      this.errorDialogVisible = true;
    }

    if(this.emailVerificationStep){
      router.push({ name: 'fetch-account-details' });
    } else if(this.detailVerificationStep) {
      this.selectedTitle = this.settings.attributes.title3;
      this.selectedSubTitle = this.settings.attributes.subtitle3;
      this.conditionalDisplay = true;
    } else if(this.isFulFillSync && !this.currentUser.extra_info.educate_created_sync) {
      this.selectedTitle = this.settings.attributes.title2;
      this.selectedSubTitle = this.settings.attributes.subtitle2;
      if(!this.isSynced) { this.conditionalDisplay = true; }
      this.conditionalDisplay2 = true;
    } else {
      this.selectedTitle = this.settings.attributes.title;
      this.selectedSubTitle = this.settings.attributes.subtitle;
      this.conditionalDisplay2 = true;
    }
  },

  computed: {
    partner() {
      return this.$store.state.partners.currentPartner;
    },

    settings() {
      return this.partner.settings.sections.user_edit_page;
    },

    contactSettings() {
      return this.partner.settings.sections.virtual_audit_user_profile_contact;
    },

    isFulFillSync() {
      return this.$store.state.users.currentUser.extra_info?.is_fulfill_sync;
    },

    fulfill_sync_details() {
      return this.partner.settings.fulfill_sync_details && this.partner.settings.fulfill_sync_details.allowed;
    },

    emailVerificationStep() {
      return this.$store.state.users.currentUser.extra_info?.verification_step === 'email_search';
    },

    detailVerificationStep() {
      return this.$store.state.users.currentUser.extra_info?.verification_step === 'detail_search';
    },

    isSynced() {
      const syncedId = this.$store.state.users.currentUser.fulfill_account_id;
      return syncedId && syncedId.length > 1;
    },

    partnerColor() {
      const el = document.createElement('span');
      el.className = 'egx-partner-color';
      document.body.appendChild(el);
      const color = this.partner.settings.sections.custom_css["--egx-partner-color"]
      document.body.removeChild(el);
      return color;
    },

    loadingScreenContent() {
      return {
        title: {
          text: 'Updating account details',
          class: ['egx-partner-color'],
          style: 'color: var(--egx-partner-color)',
        },
        description: {
          text: 'Please wait while we update your account details.'
        },
        spinner:{
          class: ['egx-partner-color']
        },
        cta: {
          text: 'If this page doesn\'t refresh, please try again.',
          class: ['egx-partner-color'],
          path_name: 'buildings'
        }
      }
    },
    currentUser() {
      return this.$store.state.users.currentUser;
    },
  },

  methods: {
    redirectToBuildingLists() {
      this.isLoading = false;
      this.$store.dispatch('buildings/setNew').then(() => {
        if(this.$store.state.users.currentUser.extra_info.programId) {
            router.push({ name: 'applicationsNew', query: { programId: this.$store.state.users.currentUser.extra_info.programId } });
          } else {
            router.push({ name: 'buildings-list' });
          }
      });
    },

    loadingScreen(val){
      this.isLoading = val;
    },

    setContactNumber(count){
      this.contactCount = count;
    },

    closeDialog() {
      this.errorDialogVisible = false;
    },
  },
};
</script>

<style scoped>
@import '../../assets/stylesheets/components/project/confirm-dialog.scss';
.readonly-input {
  display: block;
  width: 37.5rem;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 0 !important;
  appearance: none;
}

.confirm-button {
  margin-left: 10rem;
  margin-right: 10rem;
}

.useredit-title-responsive {
  flex-wrap: wrap;
  gap: .25rem .5rem;
}

.useredit-title-responsive .title-h1 {
  min-width: 0;
  max-width: 100%;
  margin: 0;
}

.useredit-title-responsive .email-title {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
}

@media (max-width: 600px) {
  .useredit-title-responsive {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
  .useredit-title-responsive h1 {
    margin-right: 0 !important;
    margin-bottom: 0.5rem;
  }
}
</style>
